import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { CoachAvatars } from "./_shared";
import { ChevronLeft, Sparkles, Users, ArrowRightCircle, Loader2 } from "lucide-react";

export default function CollabDiscover() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("client_collab_discover_title"));
  const [pairs, setPairs] = useState([]);
  const [starter, setStarter] = useState(null);
  const [starterEmotion, setStarterEmotion] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/client/collab/discover"); setPairs(data?.pairs || []); } catch { setPairs([]); }
    try { const { data: sp } = await api.get("/client/collab/starter-pick"); setStarter(sp?.offering || null); setStarterEmotion(sp?.matched_emotion || null); } catch { setStarter(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-discover">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("client_collab_discover_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("client_collab_discover_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {starter ? (
            <button data-testid="collab-starter-pick" onClick={() => navigate(`/app/collab-package-slots/${starter.id}`)}
              className="block w-full rounded-2xl border border-primary bg-primary/10 p-5 text-left">
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/50 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                <Sparkles className="h-2.5 w-2.5" /> {starterEmotion ? t("starter_card_eyebrow_matched", { emotion: t(`emo_${starterEmotion}`) }) : t("starter_card_eyebrow")}
              </span>
              <p className="mt-2 font-display text-lg text-foreground">{starter.title}</p>
              {starter.short_desc ? <p className="mt-1 text-sm text-muted-foreground">{starter.short_desc}</p> : null}
              <div className="mt-3 flex items-center gap-3">
                <CoachAvatars coaches={starter.coaches || []} size={28} />
                <span className="ml-auto font-display text-base text-primary">{eur(starter.price_cents)}</span>
                <ArrowRightCircle className="h-5 w-5 text-primary" />
              </div>
            </button>
          ) : null}

          {pairs.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("client_collab_discover_empty")}</p>
          ) : pairs.map((p, idx) => (
            <div key={`${p.coach_ids.join("-")}-${idx}`} data-testid={`client-collab-pair-${idx}`} className="rounded-2xl border border-border bg-secondary/60 p-5">
              <CoachAvatars coaches={p.coaches} size={64} />
              <p className="mt-3 font-display text-lg text-foreground">{p.coaches.map((c) => (c.name || "").trim()).filter((n) => /^[\p{L}\p{N}]/u.test(n)).join(" + ") || t("client_collab_discover_title")}</p>
              {p.offerings?.length > 0 ? (
                <div className="mt-3 space-y-1.5">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"><Sparkles className="h-3 w-3" /> {t("client_collab_discover_offerings")}</p>
                  {p.offerings.map((o) => (
                    <button key={o.id} data-testid={`discover-offering-${o.id}`} onClick={() => navigate("/app/collab/offerings")}
                      className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm">
                      <ArrowRightCircle className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="min-w-0 flex-1 truncate text-foreground">{o.title}</span>
                      <span className="font-display text-emerald-700">{eur(o.price_cents)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {p.circles?.length > 0 ? (
                <div className="mt-3 space-y-1.5">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary"><Users className="h-3 w-3" /> {t("client_collab_discover_circles")}</p>
                  {p.circles.map((c) => (
                    <button key={c.id} data-testid={`discover-circle-${c.id}`} onClick={() => p.coach_ids[0] && navigate(`/coaches/${p.coach_ids[0]}`)}
                      className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm">
                      <ArrowRightCircle className="h-3.5 w-3.5 text-primary" />
                      <span className="min-w-0 flex-1 truncate text-foreground">{c.name}</span>
                      <span className="font-display text-primary">{eur(c.price_cents)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
